<template>
  <div
    :class="{ 'border-l-4 border-l-stone-600': hasSamlChanges || hasLdapChanges }"
    class="flex flex-col space-y-6 border-t px-12 py-6"
    id="AdminSamlSettingsComponent"
  >
    <h3 class="scroll-m-20 text-2xl font-semibold tracking-tight">
      {{ tScoped("title") }}
    </h3>

    <div>
      <div
        aria-orientation="horizontal"
        class="grid h-10 w-[200px] grid-cols-2 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground"
        data-orientatation="horizontal"
        role="tablist"
        tabindex="0"
      >
        <button
          :data-state="authenticationTab === 0 ? 'active' : 'inactive'"
          class="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
          role="tab"
          type="button"
          @click="handleChangeAuthenticationTab(0)"
        >
          {{ tScoped("tabs.saml") }}
        </button>
        <button
          :data-state="authenticationTab === 1 ? 'active' : 'inactive'"
          class="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
          role="tab"
          type="button"
          @click="handleChangeAuthenticationTab(1)"
        >
          {{ tScoped("tabs.ldap") }}
        </button>
      </div>
    </div>

    <FormKit
      v-if="authenticationTab === 0"
      v-model="samlSettingsForm"
      :actions="false"
      type="form"
      @submit="handleSubmitSamlSettings"
      @submit-invalid="globals.focusFirstError"
    >
      <div class="flex flex-col space-y-6">
        <div class="grid sm:grid-cols-1 sm:space-y-4 lg:grid-cols-2 lg:space-x-4 lg:space-y-0">
          <div class="grow space-y-6 rounded-lg border p-6">
            <div>
              <h3 class="scroll-m-20 text-xl font-semibold tracking-tight">
                {{ tScoped("saml.user.title") }}
              </h3>
              <div class="formkit-help text-xs text-neutral-400">
                {{ tScoped("saml.user.description") }}
              </div>
            </div>

            <div class="flex flex-col">
              <FormKit
                :autocomplete="autoComplete"
                :classes="{
                  outer: 'basis-0 shrink-0 max-w-full',
                }"
                :label="tScoped('saml.samlMetadataUrl')"
                :validation-messages="{ isHttpUrl: 'URL must be http or https' }"
                :validation-rules="{ isHttpUrl }"
                name="SAMLMetadataURL"
                placeholder="https://example.com/metadata"
                type="text"
                validation="isHttpUrl"
              />

              <span class="mb-4 text-muted-foreground">OR</span>

              <div class="flex shrink-0 grow basis-0 space-x-2">
                <FileSelector
                  v-model="samlSettingsForm.SAMLMetadataFile"
                  :label="tScoped('saml.samlMetadataFile')"
                  accept=".xml"
                  change-button-text="Replace File"
                  delete-aria-label="Delete metadata file"
                  no-file-text="No file chosen"
                  select-button-text="Select File"
                />
              </div>
            </div>

            <hr />
            <div>
              <h3 class="text-1xl scroll-m-20 font-semibold tracking-tight">
                {{ tScoped("saml.keysAndCerts.title") }}
              </h3>
              <div class="formkit-help text-xs text-neutral-400">
                {{ tScoped("saml.keysAndCerts.description") }}
              </div>

              <div class="row mt-4 flex items-center space-x-10">
                <div class="flex w-fit flex-col space-y-2">
                  <FileSelector
                    v-model="samlSettingsForm.SAMLKeyFile"
                    :label="tScoped('saml.samlKeyFile')"
                    accept=""
                    change-button-text="Replace File"
                    delete-aria-label="Delete key file"
                    no-file-text="No file chosen"
                    select-button-text="Select File"
                  />

                  <FileSelector
                    v-model="samlSettingsForm.SAMLCertFile"
                    :label="tScoped('saml.samlCertFile')"
                    accept=""
                    change-button-text="Replace File"
                    delete-aria-label="Delete key file"
                    no-file-text="No file chosen"
                    select-button-text="Select File"
                    :show-remote-download-button="true"
                    @download-requested="handleDownloadSamlCertFile('SAMLCertFile')"
                  />
                </div>

                <div class="mt-5 flex items-center">
                  <button
                    name="GenerateNewUserSamlKeyPair"
                    class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-input bg-background px-4 py-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                    type="button"
                    @click="handleToggleGenerateUserKeyPairDialog"
                  >
                    {{ tScoped("saml.keysAndCerts.generateNewKeypair") }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="grow space-y-6 rounded-lg border p-6">
            <div>
              <h3 class="scroll-m-20 text-xl font-semibold tracking-tight">
                {{ tScoped("saml.admin.title") }}
              </h3>
              <div class="formkit-help text-xs text-neutral-400">
                {{ tScoped("saml.admin.description") }}
              </div>
            </div>

            <div class="flex flex-col">
              <FormKit
                :autocomplete="autoComplete"
                :classes="{
                  outer: 'basis-0 shrink-0 max-w-full',
                }"
                :label="tScoped('saml.samlMetadataUrl')"
                :validation-messages="{ isHttpUrl: 'URL must be http or https' }"
                :validation-rules="{ isHttpUrl }"
                name="AdminSAMLMetadataURL"
                placeholder="https://example.com/metadata"
                type="text"
                validation="isHttpUrl"
              />

              <span class="mb-4 text-muted-foreground">OR</span>

              <div class="flex shrink-0 grow basis-0 space-x-2">
                <FileSelector
                  v-model="samlSettingsForm.AdminSAMLMetadataFile"
                  :label="tScoped('saml.samlMetadataFile')"
                  accept=".xml"
                  change-button-text="Replace File"
                  delete-aria-label="Delete admin metadata file"
                  no-file-text="No file chosen"
                  select-button-text="Select File"
                />
              </div>
            </div>

            <hr />
            <div>
              <h3 class="text-1xl scroll-m-20 font-semibold tracking-tight">
                {{ tScoped("saml.keysAndCerts.title") }}
              </h3>
              <div class="formkit-help text-xs text-neutral-400">
                {{ tScoped("saml.keysAndCerts.description") }}
              </div>

              <div class="row mt-4 flex items-center space-x-10">
                <div class="flex w-fit flex-col space-y-2">
                  <FileSelector
                    v-model="samlSettingsForm.AdminSAMLKeyFile"
                    :label="tScoped('saml.samlKeyFile')"
                    accept=""
                    change-button-text="Replace File"
                    delete-aria-label="Delete key file"
                    no-file-text="No file chosen"
                    select-button-text="Select File"
                  />

                  <FileSelector
                    v-model="samlSettingsForm.AdminSAMLCertFile"
                    :label="tScoped('saml.samlCertFile')"
                    accept=""
                    change-button-text="Replace File"
                    delete-aria-label="Delete key file"
                    no-file-text="No file chosen"
                    select-button-text="Select File"
                    :show-remote-download-button="true"
                    @download-requested="handleDownloadSamlCertFile('AdminSAMLCertFile')"
                  />
                </div>

                <div class="mt-5 flex items-center">
                  <button
                    name="GenerateNewAdminSamlKeyPair"
                    class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-input bg-background px-4 py-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                    type="button"
                    @click="handleToggleGenerateAdminKeyPairDialog"
                  >
                    {{ tScoped("saml.keysAndCerts.generateNewKeypair") }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="min-w-[465px] space-y-4 rounded-lg border p-6">
          <div>
            <h3 class="scroll-m-20 text-xl font-semibold tracking-tight">
              {{ tScoped("saml.publicUrl.title") }}
            </h3>
            <div class="formkit-help text-xs text-neutral-400">
              {{ tScoped("saml.publicUrl.description") }}
            </div>
          </div>
          <FormKit
            :autocomplete="autoComplete"
            :classes="{
              outer: 'max-w-full md:max-w-[50%]',
            }"
            :validation-messages="{ isHttpUrl: 'URL must be http or https' }"
            :validation-rules="{ isHttpUrl }"
            label="URL"
            name="PublicURL"
            type="text"
            validation="isHttpUrl"
          />
        </div>

        <div class="flex justify-end space-x-2">
          <button
            v-if="hasSamlChanges"
            class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-input bg-background px-4 py-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
            type="button"
            @click="handleCancelSamlSettings"
          >
            {{ t("common.cancel") }}
          </button>

          <button
            :disabled="!hasSamlChanges"
            :aria-disabled="!hasSamlChanges"
            class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
            type="submit"
          >
            {{ t("common.save") }}
          </button>
        </div>
      </div>
    </FormKit>

    <FormKit
      v-if="authenticationTab === 1"
      v-model="ldapSettingsForm"
      :actions="false"
      type="form"
      @submit="handleSubmitLdapSettings"
      @submit-invalid="globals.focusFirstError"
    >
      <FormKit
        :autocomplete="autoComplete"
        :classes="{
          outer: 'max-w-full md:max-w-[50%]',
        }"
        :validation-messages="{ isLdapUrl: 'URL must be ldap or ldaps' }"
        :validation-rules="{ isLdapUrl }"
        :label="tScoped('ldap.ldapUrl')"
        name="LDAPURL"
        placeholder="ldap://ldap.knocknoc.io"
        type="text"
        validation="isLdapUrl"
      />

      <FormKit :label="tScoped('ldap.ldapStartTLS')" name="LDAPStartTLS" type="checkbox" />

      <FormKit :label="tScoped('ldap.ldapSkipVerifyTLS')" name="LDAPSkipVerifyTLS" type="checkbox" />

      <div class="flex justify-end space-x-2">
        <button
          :disabled="!hasLdapChanges"
          :aria-disabled="!hasLdapChanges"
          class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-input bg-background px-4 py-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
          type="button"
          @click="handleCancelLdapSettings"
        >
          {{ t("common.cancel") }}
        </button>

        <button
          :disabled="!hasLdapChanges"
          :aria-disabled="!hasLdapChanges"
          class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
          type="submit"
        >
          {{ t("common.save") }}
        </button>
      </div>
    </FormKit>
    <ErrorNotification
      :error="errorMessage"
      :title="t('msg.error')"
      @update:error="(input: string) => (errorMessage = input)"
    />
  </div>

  <Dialog :show="isGenerateNewUserKeyPairDialogOpen">
    <DialogOverlay />

    <DialogPanel name="GenerateNewUserSamlKeyPairDialog">
      <DialogTitle>Are you absolutely sure?</DialogTitle>

      <DialogDescription>
        This action cannot be undone. This will permanently delete your
        <br />current key pair and generate a new one.
      </DialogDescription>

      <div class="flex justify-end space-x-2">
        <button
          name="CancelGenerateUserSamlKeyPair"
          :disabled="isGeneratingUserSamlKeyPair"
          class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-input bg-background px-4 py-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 sm:mt-0 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
          type="button"
          @click="handleToggleGenerateUserKeyPairDialog"
        >
          {{ t("common.cancel") }}
        </button>

        <button
          name="ConfirmGenerateUserSamlKeyPair"
          :disabled="isGeneratingUserSamlKeyPair"
          class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
          type="button"
          @click="handleGenerateUserSamlKeyPair"
        >
          <ArrowPathIcon v-if="isGeneratingUserSamlKeyPair" class="animate-spin ease-in-out" />
          {{ t("common.continue") }}
        </button>
      </div>
    </DialogPanel>
  </Dialog>

  <Dialog :show="isGenerateNewAdminKeyPairDialogOpen">
    <DialogOverlay />

    <DialogPanel name="GenerateNewAdminSamlKeyPairDialog">
      <DialogTitle>Are you absolutely sure?</DialogTitle>

      <DialogDescription>
        This action cannot be undone. This will permanently delete your
        <br />current key pair and generate a new one.
      </DialogDescription>

      <div class="flex justify-end space-x-2">
        <button
          name="CancelGenerateAdminSamlKeyPair"
          :disabled="isGeneratingAdminSamlKeyPair"
          class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-input bg-background px-4 py-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 sm:mt-0 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
          type="button"
          @click="handleToggleGenerateAdminKeyPairDialog"
        >
          {{ t("common.cancel") }}
        </button>

        <button
          name="ConfirmGenerateUserSamlKeyPair"
          :disabled="isGeneratingAdminSamlKeyPair"
          class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
          type="button"
          @click="handleGenerateAdminSamlKeyPair"
        >
          <ArrowPathIcon v-if="isGeneratingAdminSamlKeyPair" class="animate-spin ease-in-out" />
          {{ t("common.continue") }}
        </button>
      </div>
    </DialogPanel>
  </Dialog>
  <InfoNotification :text="infoMessage" @update:info="(input: string) => (infoMessage = input)" />
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";

import { DialogDescription } from "@headlessui/vue";
import { ArrowPathIcon } from "@heroicons/vue/24/outline";
import { useI18n } from "vue-i18n";

import globals from "../../../globals";
import userService from "../../../services/user.service.ts";
import { useUserStore } from "../../../pinia/user.ts";

import { Dialog, DialogOverlay, DialogPanel, DialogTitle } from "../../../components/ui/dialog";
import ErrorNotification from "../../../components/ErrorNotification.vue";
import { isEqual } from "lodash";
import cloneDeep from "lodash/cloneDeep";
import FileSelector from "../Components/FileSelector.vue";
import InfoNotification from "../../../components/InfoNotification.vue";

const tScoped = (key) => {
  const scope = "pages.settings.sections.authentication";
  return t(`${scope}.${key}`);
};

const userStore = useUserStore();

const { t } = useI18n();

const errorMessage = ref("");
const infoMessage = ref("");

const settings = ref(null);
const originalSamlSettingsForm = ref({});
const originalLdapSettingsForm = ref({});

const autoComplete = import.meta.env?.VITE_AUTOCOMPLETE || "off";

function isHttpUrl({ value }) {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

function isLdapUrl({ value }) {
  try {
    const url = new URL(value);
    return ["ldap:", "ldaps:", "ldapi:", "cldap:"].includes(url.protocol);
  } catch {
    return false;
  }
}

const authenticationTab = ref(0);
function handleChangeAuthenticationTab(index) {
  authenticationTab.value = index;
}

const samlSettingsForm = ref({});
// Computed property to check if SAML settings have changed
const hasSamlChanges = computed(() => {
  return !isEqual(samlSettingsForm.value, originalSamlSettingsForm.value);
});

const isGenerateNewUserKeyPairDialogOpen = ref(false);
function handleToggleGenerateUserKeyPairDialog() {
  isGenerateNewUserKeyPairDialogOpen.value = !isGenerateNewUserKeyPairDialogOpen.value;
}

const isGeneratingUserSamlKeyPair = ref(false);
function handleGenerateUserSamlKeyPair() {
  isGeneratingUserSamlKeyPair.value = true;
  userService.generateUserSamlKeyCert().then(
    () => {
      fetchData();
      isGenerateNewUserKeyPairDialogOpen.value = false;
      isGeneratingUserSamlKeyPair.value = false;
      infoMessage.value = tScoped("saml.keysAndCerts.userGenerated");
    },
    (error) => (errorMessage.value = globals.renderError(t, error)),
  );
}

const isGenerateNewAdminKeyPairDialogOpen = ref(false);
function handleToggleGenerateAdminKeyPairDialog() {
  isGenerateNewAdminKeyPairDialogOpen.value = !isGenerateNewAdminKeyPairDialogOpen.value;
}

const isGeneratingAdminSamlKeyPair = ref(false);
function handleGenerateAdminSamlKeyPair() {
  isGeneratingAdminSamlKeyPair.value = true;
  userService.generateAdminSamlKeyCert().then(
    () => {
      fetchData();
      isGenerateNewAdminKeyPairDialogOpen.value = false;
      isGeneratingAdminSamlKeyPair.value = false;
      infoMessage.value = tScoped("saml.keysAndCerts.adminGenerated");
    },
    (error) => (errorMessage.value = globals.renderError(t, error)),
  );
}
// Function to reset SAML form to original values
function handleCancelSamlSettings() {
  samlSettingsForm.value = cloneDeep(originalSamlSettingsForm.value);
}

async function handleSubmitSamlSettings(fields) {
  await handleSubmitSettings({
    ...settings.value,

    SAMLMetadataURL: fields.SAMLMetadataURL,
    SAMLMetadataFile: fields.SAMLMetadataFile?.file?.contents,
    SAMLKeyFile: fields.SAMLKeyFile?.file?.contents,
    RemoveSAMLKeyFile: fields.SAMLKeyFile?.removeExistingFile,
    SAMLCertFile: fields.SAMLCertFile?.file?.contents,
    RemoveSAMLCertFile: fields.SAMLCertFile?.removeExistingFile,

    AdminSAMLMetadataURL: fields.AdminSAMLMetadataURL,
    AdminSAMLMetadataFile: fields.AdminSAMLMetadataFile?.file?.contents,
    AdminSAMLKeyFile: fields.AdminSAMLKeyFile?.file?.contents,
    RemoveAdminSAMLKeyFile: fields.AdminSAMLKeyFile?.removeExistingFile,
    AdminSAMLCertFile: fields.AdminSAMLCertFile?.file?.contents,
    RemoveAdminSAMLCertFile: fields.AdminSAMLCertFile?.removeExistingFile,
    PublicURL: fields.PublicURL,
  });
}

async function handleDownloadSamlCertFile(certName: string) {
  try {
    const response = await userService.getAuthenticationSettingsSamlCertDownload(certName);

    // Try to pull the filename out of the content-disposition
    let filename = "knocknoc-saml.crt"; // default fallback
    const contentDisposition = response.headers["content-disposition"];
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, "");
      }
    }

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();

    // Cleanup
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    errorMessage.value = globals.renderError(t, error);
  }
}

const ldapSettingsForm = ref({});

const hasLdapChanges = computed(() => {
  return !isEqual(ldapSettingsForm.value, originalLdapSettingsForm.value);
});

function handleCancelLdapSettings() {
  ldapSettingsForm.value = cloneDeep(originalLdapSettingsForm.value);
}

async function handleSubmitLdapSettings(fields) {
  await handleSubmitSettings({
    ...settings.value,
    LDAPURL: fields.LDAPURL,
    LDAPStartTLS: fields.LDAPStartTLS,
    LDAPSkipVerifyTLS: fields.LDAPSkipVerifyTLS,
  });
}

async function handleSubmitSettings(s) {
  try {
    await userService.updateAuthenticationSettings(s);
    infoMessage.value = tScoped("updated");
    fetchData();
  } catch (error) {
    fetchData();
    errorMessage.value = globals.renderError(t, error);
  }
}

function fetchData() {
  userStore.fetchAuthSettings({ force: true }).then(
    (response) => {
      settings.value = response.data;

      const samlFormData = {
        SAMLMetadataURL: response.data.SAMLMetadataURL,
        SAMLMetadataFile: response.data.SAMLMetadataFile
          ? { file: { name: "metadata_file.xml", contents: response.data.SAMLMetadataFile, type: "application/xml" } }
          : {},
        SAMLKeyFile: response.data.SAMLKeyFileSet ? { file: { name: "File provided" } } : {},
        SAMLCertFile: response.data.SAMLCertFileSet ? { file: { name: "File provided" } } : {},

        AdminSAMLMetadataURL: response.data.AdminSAMLMetadataURL,
        AdminSAMLMetadataFile: response.data.AdminSAMLMetadataFile
          ? {
              file: {
                name: "metadata_file.xml",
                contents: response.data.AdminSAMLMetadataFile,
                type: "application/xml",
              },
            }
          : {},
        AdminSAMLKeyFile: response.data.AdminSAMLKeyFileSet ? { file: { name: "File provided" } } : {},
        AdminSAMLCertFile: response.data.AdminSAMLCertFileSet ? { file: { name: "File provided" } } : {},

        PublicURL: response.data.PublicURL || window.location.origin,
      };

      const ldapFormData = {
        LDAPURL: response.data.LDAPURL,
        LDAPStartTLS: response.data.LDAPStartTLS,
        LDAPSkipVerifyTLS: response.data.LDAPSkipVerifyTLS,
      };

      // Set both current and original forms
      samlSettingsForm.value = cloneDeep(samlFormData);
      originalSamlSettingsForm.value = cloneDeep(samlFormData);

      ldapSettingsForm.value = cloneDeep(ldapFormData);
      originalLdapSettingsForm.value = cloneDeep(ldapFormData);
    },
    (error) => {
      errorMessage.value = globals.renderError(t, error);
    },
  );
}

onMounted(() => {
  fetchData();
});
</script>
